'use client';

import { useLiveQuery } from '@sanity/preview-kit';
import { defaultLanguage } from '@stereoagency/constants';
import {
  researchPlatformArticlesQuery,
  researchPlatformJobsQuery,
  researchPlatformPageQuery,
  researchPlatformProfilesQuery,
  researchPlatformProjectsQuery
} from './researchPlatformPageQuery';
import ResearchPlatformPage from './ResearchPlatformPage.client';
import { findInKeyArray } from '@/utils';
import { PageSections } from '@/components/layout';
import { diseasesForResearchPlatformQuery } from './diseasesForResearchPlatformQuery';

interface Props {
  id: string;
  lang: string;
}

export default function DraftModeResearchPlatformPage({ id, lang }: Props) {
  const [data] = useLiveQuery<(typeof researchPlatformPageQuery)[1] | null>(
    null,
    researchPlatformPageQuery[0],
    { id, slug: null }
  );

  const [profiles] = useLiveQuery<
    (typeof researchPlatformProfilesQuery)[1] | null
  >(null, researchPlatformProfilesQuery[0], { id, slug: null });

  const [articles] = useLiveQuery<
    (typeof researchPlatformArticlesQuery)[1] | null
  >(null, researchPlatformArticlesQuery[0], { id, slug: null });

  const [jobs] = useLiveQuery<(typeof researchPlatformJobsQuery)[1] | null>(
    null,
    researchPlatformJobsQuery[0],
    { id, slug: null }
  );

  const [projects] = useLiveQuery<
    (typeof researchPlatformProjectsQuery)[1] | null
  >(null, researchPlatformProjectsQuery[0], { id, slug: null });

  const [diseases] = useLiveQuery<
    (typeof diseasesForResearchPlatformQuery)[1] | null
  >(null, diseasesForResearchPlatformQuery[0], { id, slug: null });

  if (data == null) {
    return null;
  }

  const aboutContent = data.aboutContent
    ? findInKeyArray(data.aboutContent, [lang, defaultLanguage.id])
    : null;

  const researchContent = data.researchContent
    ? findInKeyArray(data.researchContent, [lang, defaultLanguage.id])
    : null;

  const bioContent = data.bioContent
    ? findInKeyArray(data.bioContent, [lang, defaultLanguage.id])
    : null;

  const teamContent = data.teamContent
    ? findInKeyArray(data.teamContent, [lang, defaultLanguage.id])
    : null;

  return (
    <ResearchPlatformPage
      lang={lang}
      researchPlatform={data}
      articles={articles?.articles}
      jobs={jobs?.jobs}
      projects={projects?.projects}
      departmentProfiles={profiles?.departmentProfiles}
      researchPlatformLeaderProfile={profiles?.researchPlatformLeader}
      diseases={diseases ?? []}
      aboutContent={
        aboutContent?.value ? (
          <PageSections isPreview sections={aboutContent.value} />
        ) : null
      }
      researchContent={
        researchContent?.value ? (
          <PageSections isPreview sections={researchContent.value} />
        ) : null
      }
      bioContent={
        bioContent?.value ? (
          <PageSections isPreview sections={bioContent.value} />
        ) : null
      }
      teamContent={
        teamContent?.value ? (
          <PageSections isPreview sections={teamContent.value} />
        ) : null
      }
    />
  );
}
